/**
 * @file Button atom — data-driven action primitive (RFC §3).
 * @description Narrows the Gluestack compound Button: callers pass data
 * (label, icon, tone, size), never ButtonText/ButtonIcon trees — the
 * compound ceremony is assembled inside the atom.
 * @module components/atoms/Button
 */
import React from "react";

import {
  Button as UIButton,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
} from "@/components/ui/button";

import { deriveAriaFromAccessibilityState } from "../accessibilityState";
import { TEXT_VARIANT_CLASS } from "../types";

import type { TextVariant, WithUnsafeStyle } from "../types";

/**
 *
 */
type UIButtonProps = React.ComponentProps<typeof UIButton>;
/**
 *
 */
type IconComponent = React.ElementType;

/** Semantic intent — maps to the Gluestack action internally. */
export type ButtonTone =
  | "primary"
  | "accent"
  | "secondary"
  | "positive"
  | "danger"
  | "neutral";
/**
 * Closed semantic color tier for the inner label/icon (RFC content + status
 * tokens). This is the sanctioned, closed-enum replacement for the free-string
 * `textClassName`/`iconClassName` escape hatch: callers pick a semantic intent,
 * the atom owns the closed-token class internally (the same pattern as Stack's
 * `space` and Text's `variant`). Off-scale values are uncompilable at the type
 * level, and the classes they map to are all closed design-system tokens.
 */
export type ButtonContentTone =
  | "primary"
  | "secondary"
  | "muted"
  | "subtle"
  | "inverse"
  | "on-accent"
  | "accent"
  | "info"
  | "danger"
  | "success"
  | "warning";
/**
 *
 */
export type ButtonVariant = "solid" | "outline" | "link";
/**
 *
 */
export type ButtonSize = "xs" | "sm" | "md" | "lg";

const TONE_TO_ACTION: Record<
  ButtonTone,
  "primary" | "secondary" | "positive" | "negative"
> = {
  primary: "primary",
  // `accent` borrows primary's action for shape/state wiring and overlays the
  // accent surface below — Gluestack has no accent action of its own.
  accent: "primary",
  secondary: "secondary",
  positive: "positive",
  danger: "negative",
  // neutral has no Gluestack action of its own; base it on "secondary" and
  // overlay the closed neutral surface classes via TONE_TO_CLASS below.
  neutral: "secondary",
};

/**
 * Closed tone → fixed surface class. `neutral` and `accent` need one:
 * the other tones are fully expressed by their Gluestack `action`. `neutral`
 * renders a quiet muted surface (with a web hover strengthen) and a
 * content-primary label — every value is a closed design-system semantic token.
 * The closure guard is the exhaustive per-tone unit test in `__tests__`.
 *
 * ## Why every tone here overrides hover TWICE
 *
 * The Gluestack action underneath ships its own hover on the `data-[hover=true]`
 * key — `bg-primary-600` for the action `accent` borrows, `bg-secondary-600` for
 * `neutral`'s. A `web:hover:` class does NOT override it: the two are different
 * variant keys, so tailwind-merge sees no conflict and keeps BOTH, leaving which
 * one paints to CSS ordering. Those ramp values are the wrong colour entirely —
 * `primary-500` is near-black in light mode and near-white in dark (which is why
 * the rest surface is overridden at all), so an accent-blue button was washing
 * out to a pale slab on hover in dark mode instead of deepening.
 *
 * So each tone states its hover on both keys: `data-[hover=true]:` to actually
 * beat the Gluestack class it conflicts with, and `web:hover:` so the colour
 * still holds if the JS-driven hover state ever strands (which it has before).
 * Both name the same token, so there is nothing for them to disagree about.
 *
 * ## These surfaces apply to `variant="link"` TOO
 *
 * This map is merged into the root className for every variant. The `link`
 * variant only zeroes the padding (`px-0` in the tva) — it does not clear a
 * background — so `variant="link" tone="accent"` is not a text link at all: it
 * is a full accent-blue slab. Pair it with `labelTone="accent"` and the label
 * is blue text on blue fill, i.e. invisible (SE-6554, the stage editor's
 * confirm button; Raniel, 2026-08-06).
 *
 * A link that needs accent-coloured TEXT wants the default `primary` tone with
 * `labelTone="accent"`, not `tone="accent"`. The behaviour is left as-is rather
 * than special-cased for `link`, because `neutral`'s muted fill on a link is
 * load-bearing where it is already used (`AddPlayersRowView`).
 */
const TONE_TO_CLASS: Partial<Record<ButtonTone, string>> = {
  neutral:
    "bg-surface-muted web:hover:bg-surface-strong data-[hover=true]:bg-surface-strong data-[active=true]:bg-surface-strong",
  // The product's blue CTA. Gluestack's `primary` action is `primary-500`,
  // which is near-black in light mode and near-white in dark; the Figma
  // confirm button is `accent-primary` (SE-6233).
  //
  // The hover and pressed shades are the design system's own `accent-hover` and
  // `accent-pressed`, not an alpha of the resting blue. An alpha fill is not a
  // shade — it is the blue COMPOSITED with whatever happens to sit behind the
  // button, so the same class lands on a different colour on a raised toolbar
  // than on a card, and lightens in light mode while darkening in dark. The
  // tokens carry an explicit value per theme, and give pressed a shade of its
  // own instead of repeating hover's (which left the button with no press
  // feedback at all).
  accent:
    "bg-accent-primary border-accent-primary web:hover:bg-accent-hover web:hover:border-accent-hover data-[hover=true]:bg-accent-hover data-[hover=true]:border-accent-hover data-[active=true]:bg-accent-pressed data-[active=true]:border-accent-pressed",
};

/**
 * The fill `TONE_TO_CLASS` gives a tone, for a variant. `neutral` is a muted
 * SLAB, which is right for solid and wrong for outline: Figma's
 * `variant=outline, tone=neutral` (986:33442) is transparent with a
 * `palette/secondary/300` border — exactly what the vendored `outline` variant
 * already paints for the `secondary` action neutral borrows. Merging the slab
 * on top made every neutral outline button read as grey, which is how the
 * selection bar's "Copy to another list" came to look disabled (SE-8526).
 * @param tone - The button tone.
 * @param variant - The button variant.
 * @returns The tone's surface classes, or undefined when the variant owns them.
 */
const toneSurfaceClass = (
  tone: ButtonTone,
  variant: ButtonVariant
): string | undefined =>
  tone === "neutral" && variant === "outline" ? undefined : TONE_TO_CLASS[tone];

/**
 * The label/icon tone a tone pairs with when the caller names none. The accent
 * tone overrides the surface but borrows the primary action for state wiring,
 * whose foreground is near-black in light mode — a black label on blue — so it
 * pairs its own (SE-6501). Outline/neutral's vendored foreground is
 * `content-muted`; Figma (986:33442) labels it `content/primary` (SE-8526).
 * @param tone - The button tone.
 * @param variant - The button variant.
 * @returns The default content tone, or undefined for the tva default.
 */
const defaultContentToneFor = (
  tone: ButtonTone,
  variant: ButtonVariant
): ButtonContentTone | undefined => {
  if (tone === "accent") return "on-accent";
  if (tone === "neutral" && variant === "outline") return "primary";
  return undefined;
};

/**
 * A dropdown trigger that must read as an outline/neutral/sm Button (SE-8526:
 * the selection bar's "More ▾"). A trigger is the dropdown shell's own
 * measurable pressable, so it cannot BE a Button — nesting one would put a
 * button inside a button on web. The classes live here because the border is
 * the vendored `secondary-300` shade, which only the atom layer may name.
 */
export const BUTTON_OUTLINE_NEUTRAL_TRIGGER_CLASS =
  "h-9 flex-row items-center justify-center gap-2 rounded-sm border border-secondary-300 bg-transparent px-4 web:hover:bg-surface-raised";

/**
 * Accessible-name contract: a button must carry a visible `label`, an
 * `accessibilityLabel`, or both — icon-only buttons without either are
 * uncompilable (RFC core principle).
 */
type ButtonA11yLabel =
  | { /** Visible label. */ label: string; accessibilityLabel?: string }
  | {
      /** Icon-only buttons: no visible label, so the a11y label is required. */
      label?: never;
      accessibilityLabel: string;
    };

/**
 *
 */
interface ButtonOwnProps
  extends
    Omit<
      UIButtonProps,
      | "action"
      | "variant"
      | "size"
      | "style"
      | "children"
      | "accessibilityLabel"
    >,
    WithUnsafeStyle<UIButtonProps["style"]> {
  /** Semantic intent (danger maps to Gluestack "negative"). */
  tone?: ButtonTone;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Optional Lucide icon rendered beside the label. */
  icon?: IconComponent;
  iconPosition?: "leading" | "trailing";
  /** Renders a spinner in place of the icon and disables the button. */
  isLoading?: boolean;
  /**
   * Closed semantic color tier for the label text (RFC content/status tokens).
   * The atom forwards it to a closed-token class internally — this is the
   * sanctioned replacement for `textClassName`. Omit for the variant default.
   */
  labelTone?: ButtonContentTone;
  /**
   * Closed semantic color tier for the icon — same closed enum as `labelTone`.
   * The sanctioned replacement for color-only `iconClassName` overrides.
   */
  iconTone?: ButtonContentTone;
  /**
   * Closed typography for the label, independent of the button `size`. Maps to
   * the design system's bundled Text `variant` classes (the sanctioned
   * replacement for typographic `textClassName` overrides).
   */
  labelVariant?: TextVariant;
  /**
   * Closed icon size, independent of the button `size` (the sanctioned
   * replacement for sizing-related `iconClassName` overrides).
   */
  iconSize?: ButtonIconSize;
  /**
   * Closed button shape (`rounded` default, `pill`, `circle`). The atom owns
   * the fixed radius/aspect class — no free radius className.
   */
  shape?: ButtonShape;
  /**
   * Icon-only layout: removes the label gap and uses square padding so a lone
   * icon centers. A fixed boolean, not a style escape hatch.
   */
  iconOnly?: boolean;
  /**
   * Truncate the label after this many lines (an integer). The atom hard-codes
   * `ellipsizeMode="tail"` — callers only choose the line count.
   */
  labelNumberOfLines?: number;
}

/**
 * Closed semantic tone → closed-token class. Every value is a design-system
 * semantic token (content/status tier). Because semantic families are
 * lint-whitelisted (the tailwind theme is their source of truth) and this map
 * is a plain literal the tailwind plugin does not scan, the closure guard for
 * these values is the exhaustive per-tone unit test in `__tests__` — it fails
 * if any entry drifts to raw palette, an arbitrary value, or a typo'd token.
 *
 * ## Why every tone states its colour FOUR times
 *
 * The same both-keys trap as `TONE_TO_CLASS` above, one layer down — this one
 * bites the TEXT rather than the fill. The Gluestack `buttonText` variant
 * ships its own per-state foreground, e.g. solid/primary carries
 * `text-content-inverse data-[hover=true]:text-content-inverse
 * data-[active=true]:text-content-inverse`. A bare `text-*` class from this
 * map sits on the DEFAULT key, so tailwind-merge sees no conflict with the
 * `data-[hover=true]:` one and keeps both: the label paints correctly at rest
 * and then flips to Gluestack's colour the moment the button is hovered or
 * pressed.
 *
 * That is a real regression, not a cosmetic one. `content-inverse` is
 * `typography-0`, which is 254,254,255 in light mode but 23,23,23 in DARK — so
 * the white label on the accent-blue CTA turned BLACK on hover in dark mode
 * (Raniel, 2026-08-06: the tag modal's `+ Add` and the toolbar's
 * `+ Add player`).
 *
 * So each tone names its colour on all four keys — default, `web:hover:`,
 * `data-[hover=true]:` and `data-[active=true]:` — and they all name the same
 * token, so there is nothing for them to disagree about. The strings are
 * written out literally rather than composed, because a class built by
 * concatenation is invisible to the class scanner and would never be emitted.
 */
const CONTENT_TONE_TO_CLASS: Record<ButtonContentTone, string> = {
  primary:
    "text-content-primary web:hover:text-content-primary data-[hover=true]:text-content-primary data-[active=true]:text-content-primary",
  secondary:
    "text-content-secondary web:hover:text-content-secondary data-[hover=true]:text-content-secondary data-[active=true]:text-content-secondary",
  muted:
    "text-content-muted web:hover:text-content-muted data-[hover=true]:text-content-muted data-[active=true]:text-content-muted",
  subtle:
    "text-content-subtle web:hover:text-content-subtle data-[hover=true]:text-content-subtle data-[active=true]:text-content-subtle",
  inverse:
    "text-content-inverse web:hover:text-content-inverse data-[hover=true]:text-content-inverse data-[active=true]:text-content-inverse",
  "on-accent":
    "text-content-on-accent web:hover:text-content-on-accent data-[hover=true]:text-content-on-accent data-[active=true]:text-content-on-accent",
  accent:
    "text-accent-primary web:hover:text-accent-primary data-[hover=true]:text-accent-primary data-[active=true]:text-accent-primary",
  info: "text-status-info web:hover:text-status-info data-[hover=true]:text-status-info data-[active=true]:text-status-info",
  danger:
    "text-status-error web:hover:text-status-error data-[hover=true]:text-status-error data-[active=true]:text-status-error",
  success:
    "text-status-success web:hover:text-status-success data-[hover=true]:text-status-success data-[active=true]:text-status-success",
  warning:
    "text-status-warning web:hover:text-status-warning data-[hover=true]:text-status-warning data-[active=true]:text-status-warning",
};

/**
 * Closed icon-size token → closed sizing class for the inner icon. Lets callers
 * size the button icon independently of the label without a free className.
 */
export type ButtonIconSize = "xs" | "sm" | "md" | "lg";
const ICON_SIZE_TO_CLASS: Record<ButtonIconSize, string> = {
  xs: "size-3",
  sm: "size-4",
  md: "size-5",
  lg: "size-6",
};

/**
 * Closed button-shape enum → fixed radius/aspect class. `rounded` is the
 * DEFAULT and emits `rounded-md`; `pill` is fully rounded; `circle` is a fully
 * rounded square with no padding (for icon-only round buttons). The atom owns
 * the classes — callers never pass a free radius className.
 *
 * **`rounded` used to mean "no class", on the stated ground that it "keeps the
 * tva default radius". There is no such default (SE-7841).** The vendored
 * Gluestack base carries a bare `rounded`, which resolves against
 * `borderRadius.DEFAULT` — and `tailwind.config.js` defines `borderRadius`
 * directly under `theme`, not under `extend`, so it REPLACES Tailwind's scale
 * and only `none/sm/md/lg/xl/full` exist. Compiling the project's CSS emits
 * `.rounded-sm`, `.rounded-md` and `.rounded-full` and no `.rounded` rule at
 * all, so every button that omitted `shape` rendered with square corners.
 *
 * That was 179 of the 186 files rendering `<Button>`. Figma binds the CTA it
 * was first noticed on (SE-7854, node `247:2792`) to `radius-md` = 8, which is
 * what `rounded` now produces by default. A caller that genuinely wants square
 * corners has no shape for it today; add one rather than removing the default.
 */
export type ButtonShape = "rounded" | "pill" | "circle";
const SHAPE_TO_CLASS: Record<ButtonShape, string> = {
  rounded: "rounded-md",
  pill: "rounded-full",
  circle: "rounded-full aspect-square p-0",
};

/**
 * Fixed icon-only layout: square padding and no inter-child gap, so a single
 * icon sits centered without the label's leading/trailing space.
 */
const ICON_ONLY_CLASS = "gap-0 p-2";

/**
 * Joins the closed-token classes a caller selected (tone + typography/size);
 * returns `undefined` when none are set so the inner element keeps its tva
 * default. Every input is already a closed design-system token.
 * @param classes - Closed-token classes (falsy entries ignored).
 * @returns The combined className, or `undefined` when none are set.
 */
const composeClass = (
  ...classes: Array<string | undefined>
): string | undefined => classes.filter(Boolean).join(" ") || undefined;

/**
 * Public Button atom props — own props plus the accessible-name contract
 * (`label` and/or `accessibilityLabel` is required at the type level).
 */
export type ButtonProps = ButtonOwnProps & ButtonA11yLabel;

const Button = React.forwardRef<
  React.ComponentRef<typeof UIButton>,
  ButtonProps
>(function Button(
  {
    label,
    tone = "primary",
    variant = "solid",
    size = "md",
    icon,
    iconPosition = "leading",
    isLoading = false,
    isDisabled,
    labelTone,
    iconTone,
    labelVariant,
    iconSize,
    // Defaults to `rounded` (SE-7841): omitting it used to mean NO radius at
    // all, not the design's 8px. See ButtonShape above.
    shape = "rounded",
    iconOnly = false,
    labelNumberOfLines,
    className,
    UNSAFE_style,
    accessibilityState,
    ...props
  },
  ref
) {
  const showLeading = icon && iconPosition === "leading" && !isLoading;
  const showTrailing = icon && iconPosition === "trailing" && !isLoading;
  const defaultContentTone = defaultContentToneFor(tone, variant);
  const resolvedLabelTone = labelTone ?? defaultContentTone;
  const resolvedIconTone = iconTone ?? defaultContentTone;
  const labelClass = composeClass(
    resolvedLabelTone ? CONTENT_TONE_TO_CLASS[resolvedLabelTone] : undefined,
    labelVariant ? TEXT_VARIANT_CLASS[labelVariant] : undefined
  );
  const iconClass = composeClass(
    resolvedIconTone ? CONTENT_TONE_TO_CLASS[resolvedIconTone] : undefined,
    iconSize ? ICON_SIZE_TO_CLASS[iconSize] : undefined
  );
  const rootClass = composeClass(
    toneSurfaceClass(tone, variant),
    SHAPE_TO_CLASS[shape],
    iconOnly ? ICON_ONLY_CLASS : undefined,
    className
  );
  return (
    <UIButton
      ref={ref}
      action={TONE_TO_ACTION[tone]}
      variant={variant}
      size={size}
      isDisabled={isDisabled || isLoading}
      className={rootClass}
      style={UNSAFE_style}
      accessibilityState={accessibilityState}
      {...deriveAriaFromAccessibilityState(accessibilityState)}
      {...props}
    >
      {isLoading ? <ButtonSpinner /> : null}
      {showLeading ? <ButtonIcon as={icon} className={iconClass} /> : null}
      {label ? (
        <ButtonText
          className={labelClass}
          numberOfLines={labelNumberOfLines}
          ellipsizeMode="tail"
        >
          {label}
        </ButtonText>
      ) : null}
      {showTrailing ? <ButtonIcon as={icon} className={iconClass} /> : null}
    </UIButton>
  );
});

Button.displayName = "Button";

export { Button };
